import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle } from "@phosphor-icons/react";
import { Header } from "@/components/site/Header";
import { ServiceArea } from "@/components/site/ServiceArea";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { getLocation, LOCATIONS } from "@/data/locations";

const SITE = "https://treasurevalleyturf.com";

const SERVICES = [
    "Artificial turf lawns",
    "Backyard putting greens",
    "Pet & play area turf",
];

export default function LocationPage() {
    const { slug } = useParams();
    const loc = getLocation(slug);

    if (!loc) return <Navigate to="/" replace />;

    const title = `Artificial Turf & Putting Greens in ${loc.city}, Idaho | Treasure Valley Turf & Greens`;
    const description = `Premium artificial turf and putting green installation in ${loc.city}, ID. PFAS-free, pet & kid-safe synthetic grass. Family-owned, free estimates, 16-year warranty. Call (208) 605-9096.`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Artificial turf installation",
            provider: { "@id": `${SITE}/#business` },
            areaServed: { "@type": "City", name: `${loc.city}, ID` },
            url: `${SITE}/service-areas/${loc.slug}`,
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE}/#contact` },
                { "@type": "ListItem", position: 3, name: loc.city, item: `${SITE}/service-areas/${loc.slug}` },
            ],
        },
    ];

    return (
        <div data-testid="location-page" className="relative min-h-screen bg-brand-bg text-brand-text">
            <Seo
                title={title}
                description={description}
                canonical={`${SITE}/service-areas/${loc.slug}`}
                jsonLd={jsonLd}
            />
            <ServiceArea />
            <Header />

            <main>
                {/* Hero */}
                <section className="relative overflow-hidden pb-16 pt-44 md:pb-24 md:pt-56">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[720px] rounded-full bg-brand-accent/10 blur-[120px]"
                    />
                    <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                        <Reveal>
                            <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-brand-accent">
                                <MapPin size={14} weight="duotone" />
                                Serving {loc.city}, Idaho
                            </p>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h1 className="font-display max-w-4xl text-balance text-4xl font-semibold leading-[1.02] tracking-tight md:text-6xl">
                                Artificial turf &amp;{" "}
                                <span className="italic text-brand-accent">putting greens</span>{" "}
                                in {loc.city}.
                            </h1>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-muted md:text-lg">
                                {loc.blurb}
                            </p>
                        </Reveal>
                        <Reveal delay={0.24}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <a
                                    href="/#quote"
                                    data-testid="location-cta-quote"
                                    className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-4 text-base font-bold text-brand-bg transition-all duration-300 hover:bg-brand-accentHover"
                                >
                                    Get a free quote in {loc.city}
                                    <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                                </a>
                                <a
                                    href="tel:+12086059096"
                                    data-testid="location-cta-call"
                                    className="inline-flex items-center gap-2 rounded-full border border-brand-text/25 px-7 py-4 text-base font-semibold text-brand-text transition-colors hover:border-brand-accent hover:text-brand-accent"
                                >
                                    Call (208) 605-9096
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* What we install */}
                <section className="border-t border-brand-border/60 bg-white py-20 md:py-28">
                    <div className="mx-auto max-w-7xl px-6 md:px-10">
                        <Reveal>
                            <h2 className="font-display text-3xl font-medium tracking-tight md:text-5xl">
                                What we install in{" "}
                                <span className="italic text-brand-accent">{loc.city}</span>
                            </h2>
                        </Reveal>
                        <div className="mt-10 grid gap-4 md:grid-cols-3">
                            {SERVICES.map((s, i) => (
                                <Reveal key={s} delay={i * 0.07}>
                                    <div
                                        data-testid={`location-service-${i}`}
                                        className="spotlight h-full rounded-2xl border border-brand-border bg-white p-7 transition-colors duration-500 hover:border-brand-accent/40"
                                    >
                                        <span className="font-display text-sm italic text-brand-accent">0{i + 1}</span>
                                        <h3 className="font-display mt-3 text-xl font-medium">{s}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                            Custom-designed, hand-laid, and backed by a 16-year manufacturer warranty.
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.2}>
                            <p className="mt-10 max-w-3xl text-base leading-relaxed text-brand-muted">
                                {loc.detail}
                            </p>
                        </Reveal>
                        <Reveal delay={0.26}>
                            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                                {[
                                    "Free on-site measurement",
                                    "PFAS-free, pet & kid-safe materials",
                                    "In-house local crew — no subcontractors",
                                    "16-year manufacturer warranty",
                                ].map((t) => (
                                    <li key={t} className="flex items-center gap-3 text-sm text-brand-muted">
                                        <CheckCircle size={18} weight="fill" className="shrink-0 text-brand-accent" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </section>

                {/* Other areas */}
                <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                    <Reveal>
                        <h2 className="font-display text-2xl font-medium tracking-tight md:text-4xl">
                            Also serving the rest of the Valley
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {LOCATIONS.filter((l) => l.slug !== loc.slug).map((l) => (
                                <Link
                                    key={l.slug}
                                    to={`/service-areas/${l.slug}`}
                                    data-testid={`location-link-${l.slug}`}
                                    className="rounded-full border border-brand-border bg-brand-surface px-5 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent hover:text-brand-accent"
                                >
                                    {l.city}, ID
                                </Link>
                            ))}
                        </div>
                    </Reveal>
                </section>
            </main>

            <Footer />
        </div>
    );
}
